import { getQuoteLineTotalFromJob } from "../../../invoice/create/utils/quoteJobInvoiceTotals";
import { normalizeCostOverride } from "../../jobs-list/jobs-layout/components/Summary/BasicJob";
import { getGrossLineFromCostOverride } from "../../../scope-of-work/components/ListTable/components/invoiceLineFromCostOverride";
import type { Job } from "../../interfaces/job.interfaces";
import type { ScopeOfWork } from "../../../scope-of-work/types/scope-of-work.interface";

export type JobServiceCostBreakdown = {
  source: string;
  amount: number;
  unitPrice?: number;
  units?: number;
  unitLabel?: string;
  currency?: string;
  showMultiplication: boolean;
};

type QuoteServiceLine = {
  total?: number | string;
  price?: number | string;
  units?: number | string;
  measure?: string | null;
  serviceUid?: string | null;
};

function resolveJobQuoteServiceLineUid(job: Job | null | undefined): string | null {
  const quoteServiceUid = (job as { quoteServiceUid?: string | null } | undefined)?.quoteServiceUid;
  if (typeof quoteServiceUid === "string" && quoteServiceUid.trim()) return quoteServiceUid;

  const depositReservedQuoteServiceUid = (job as { depositReservedQuoteServiceUid?: string | null } | undefined)
    ?.depositReservedQuoteServiceUid;
  if (typeof depositReservedQuoteServiceUid === "string" && depositReservedQuoteServiceUid.trim()) {
    return depositReservedQuoteServiceUid;
  }

  return null;
}

function getLinkedQuoteServiceLine(job: Job): QuoteServiceLine | null {
  const quote = job.quote as { services?: QuoteServiceLine[] } | null | undefined;
  if (!Array.isArray(quote?.services)) return null;

  const lineUid = resolveJobQuoteServiceLineUid(job);
  if (lineUid) {
    return quote.services.find((service) => service?.serviceUid === lineUid) ?? null;
  }

  return quote.services[0] ?? null;
}

function formatCostByLabel(by?: string): string {
  const labels: Record<string, string> = {
    per_item: "per item",
    per_square_foot: "per sq ft",
    per_sf: "per sq ft",
    per_unit: "per unit",
    sqm: "sqm",
    unique: "one-time",
    monthly: "monthly",
    yearly: "yearly",
  };

  return labels[by ?? ""] ?? by ?? "units";
}

function fromQuoteLine(job: Job): JobServiceCostBreakdown | null {
  const fromQuote = getQuoteLineTotalFromJob(job);
  if (fromQuote <= 0) return null;

  const line = getLinkedQuoteServiceLine(job);
  const unitPrice = Number(line?.price ?? 0);
  const units = Number(line?.units ?? 0);
  const computedTotal = unitPrice > 0 && units > 0 ? unitPrice * units : fromQuote;
  const amount = Number.isFinite(computedTotal) && computedTotal > 0 ? computedTotal : fromQuote;
  const showMultiplication = unitPrice > 0 && units > 0;

  return {
    source: "Quote / job pricing",
    amount,
    unitPrice: showMultiplication ? unitPrice : undefined,
    units: showMultiplication ? units : undefined,
    unitLabel: line?.measure?.trim() || "units",
    currency: "USD",
    showMultiplication,
  };
}

function fromCostOverride(scopeOfWork?: ScopeOfWork | null): JobServiceCostBreakdown | null {
  const costOverride = normalizeCostOverride(scopeOfWork?.costOverride) as Record<string, unknown> | null;
  if (!costOverride) return null;

  const amount = getGrossLineFromCostOverride(costOverride);
  if (amount == null || amount <= 0) return null;

  const by = String(costOverride.by ?? "");
  const unitPrice = Number(costOverride.value ?? costOverride.price ?? costOverride.amount ?? 0);
  const unitsRaw = costOverride.units != null && costOverride.units !== "" ? Number(costOverride.units) : 1;
  const units = Number.isFinite(unitsRaw) && unitsRaw > 0 ? unitsRaw : 1;
  const unitLabel = String(costOverride.measure ?? "").trim() || formatCostByLabel(by);
  const isFlatFee = by === "unique" || by === "monthly" || by === "yearly";
  const showMultiplication = !isFlatFee && Number.isFinite(unitPrice) && unitPrice > 0 && units > 0;

  return {
    source: "Price override",
    amount,
    unitPrice: showMultiplication ? unitPrice : undefined,
    units: showMultiplication ? units : undefined,
    unitLabel: showMultiplication ? unitLabel : undefined,
    currency: String(costOverride.currency ?? "USD"),
    showMultiplication,
  };
}

function fromScopeAreas(job: Job, scopeOfWork?: ScopeOfWork | null): JobServiceCostBreakdown | null {
  if (job.type === "basic") return null;

  const areas = scopeOfWork?.areasByScopesOfWork;
  const typeAreas = scopeOfWork?.typeOfService?.areas;
  if (!areas?.length || !typeAreas?.length) return null;

  let sum = 0;
  let pricedLines = 0;

  for (const area of areas) {
    const serviceArea = typeAreas.find((item) => item.AreaByTypeOfService?.areaUid === area.areaUid);
    const unit = Number(serviceArea?.AreaByTypeOfService?.cost?.value ?? 0);
    const quantity = Number(area.quantity) || 1;

    if (unit > 0) {
      sum += unit * quantity;
      pricedLines += 1;
    }
  }

  if (sum <= 0) return null;

  return {
    source: pricedLines > 1 ? "Scope of work (areas)" : "Scope of work",
    amount: sum,
    showMultiplication: false,
  };
}

export function resolveJobServiceCost(job: Job, scopeOfWork?: ScopeOfWork | null): JobServiceCostBreakdown | null {
  return fromQuoteLine(job) ?? fromCostOverride(scopeOfWork) ?? fromScopeAreas(job, scopeOfWork);
}

export function formatJobServiceCostCurrency(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}
